// Generative marks derived from ids: a mirrored 5×5 bitmap (people in the ink colour,
// spaces in a colour from the server's palette). Deterministic, no network.

/** FNV-1a, then xorshift for more bits. */
export function hash32(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

function* bits(seed: string): Generator<number> {
  let x = hash32(seed) || 1
  for (;;) {
    x ^= x << 13
    x >>>= 0
    x ^= x >>> 17
    x ^= x << 5
    x >>>= 0
    for (let i = 0; i < 32; i++) yield (x >>> i) & 1
  }
}

/** 5×5 cells, left-right mirrored; never blank or full. Row-major. */
export function identicon(id: string): boolean[] {
  const g = bits(id)
  const cells: boolean[] = new Array<boolean>(25).fill(false)
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 3; x++) {
      const on = g.next().value === 1
      cells[y * 5 + x] = on
      cells[y * 5 + (4 - x)] = on
    }
  }
  const n = cells.filter(Boolean).length
  if (n === 0) cells[12] = true
  if (n === 25) cells[12] = false
  return cells
}

/**
 * A colour for a generated space mark, picked by `id` from the palette (drawn adjusted to the
 * theme, forTheme). Undefined when there are none: use ink.
 */
export function markColor(id: string, palette: readonly string[]): string | undefined {
  // Any palette colour: it's drawn adjusted to the theme (forTheme).
  const usable = palette.filter((c) => /^#[0-9a-f]{6}$/i.test(c))
  return usable.length ? usable[hash32(`${id}:color`) % usable.length] : undefined
}

/** A short hex fingerprint of an id, for data readouts. */
export function shortCode(id: string, len = 6): string {
  return hash32(id).toString(16).padStart(8, '0').slice(0, len)
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = n / 1024
  let u = 0
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024
    u++
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[u]}`
}

export function formatDuration(secs: number | null | undefined): string {
  if (!secs) return '--:--'
  const s = Math.round(secs)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = String(s % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${String(m).padStart(2, '0')}:${r}`
}

/** A player's clock: `00:07`, `03:41`, `1:02:05` (whole seconds, never rounded up). */
export function formatClock(secs: number): string {
  const s = Number.isFinite(secs) && secs > 0 ? Math.floor(secs) : 0
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = String(s % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${String(m).padStart(2, '0')}:${r}`
}
